'use client';

import { createContext, useContext, type ReactNode } from 'react';
import type { MenuLink } from './menuTypes';

/**
 * Host adapter contract for DYNAMIC menu groups — entries whose
 * children come from a backend query at render time rather than
 * static seed JSON. A menu group declared with
 * {@code dataSource: 'subscriptions'} resolves its links via this
 * adapter.
 *
 * <p>Owns all menu data including categories. Although blog/learning
 * adapters expose their own {@code listCategories} for content
 * surfaces, menu shapes differ (icons, nesting, avatar fields) and
 * the per-locale labelling lives on the menu side — so this adapter
 * has its own {@code categories()} method that may internally
 * translate from a content adapter's data or hit a different
 * backend.
 *
 * <p>Hosts implement the methods their menus reference and omit the
 * rest. The renderer treats a missing method (or one that throws) as
 * "no entries" and renders an empty group.
 *
 * <h3>Backend reality</h3>
 *
 * Categories come from our backend (LearningCategory /
 * BlogItemCategory entities). Subscriptions come from Cinema8 (or
 * the host's video platform). LiveEvents come from our backend
 * (LearningDetail rows with a startDate). ContinueWatching is mixed
 * — course progress from our backend, video position from Cinema8.
 */

/** Domain key for {@link MenuDataSource.categories}. Known values
 *  match the content modules (courses / blog / micro / video); hosts
 *  may add their own custom domains. */
export type MenuCategoryDomain = 'courses' | 'blog' | 'micro' | 'video' | (string & {});

export interface MenuDataSource {
  /** Categories formatted as menu links. Hosts return entries with
   *  href, label (localized), optional icon + count badge. Pass the
   *  domain (courses/blog/etc.) so a single host can serve multiple
   *  category taxonomies from one adapter. */
  categories?(opts: { domain: MenuCategoryDomain; signal?: AbortSignal }): Promise<MenuLink[]>;

  /** Subscribed channels — typically Cinema8-sourced. Each link
   *  surfaces the channel avatar in {@code MenuLink.avatar} and an
   *  optional {@code unread} indicator for new content. */
  subscriptions?(signal?: AbortSignal): Promise<MenuLink[]>;

  /** "Continue watching" / "Resume learning" rail items. Mixed
   *  source — course progress from our backend, video position from
   *  Cinema8. Hosts aggregate. */
  continueWatching?(opts?: { limit?: number; signal?: AbortSignal }): Promise<MenuLink[]>;

  /** Upcoming live events. Sourced from learning detail rows with a
   *  future {@code startDate}. */
  liveEvents?(opts?: { limit?: number; signal?: AbortSignal }): Promise<MenuLink[]>;

  /** Recently viewed items. */
  recentlyViewed?(opts?: { limit?: number; signal?: AbortSignal }): Promise<MenuLink[]>;

  /** User's enrolled courses formatted as menu links. */
  myCourses?(opts?: { limit?: number; signal?: AbortSignal }): Promise<MenuLink[]>;

  /** Custom escape hatch — hosts that ship private menu sources
   *  expose them through here keyed by an arbitrary identifier. The
   *  admin's {@code dataSource} field surfaces the registered keys
   *  in a picker. */
  custom?(opts: { key: string; locale?: string; signal?: AbortSignal }): Promise<MenuLink[]>;
}

const MenuDataSourceContext = createContext<MenuDataSource | null>(null);

export function MenuDataSourceProvider({
  value,
  children,
}: {
  value: MenuDataSource;
  children: ReactNode;
}) {
  return <MenuDataSourceContext.Provider value={value}>{children}</MenuDataSourceContext.Provider>;
}

export function useMenuDataSource(): MenuDataSource | null {
  return useContext(MenuDataSourceContext);
}
